import React, { Suspense } from "react"
import Image from "next/image"

import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import QuadProductImagesPreviewServerPart from "@/components/ui/components/quad-product-images-preview-server-part"

interface QuadProductImagesPreviewProps extends React.HTMLProps<HTMLDivElement> {
  productSkus?: string[]
  productCount?: number
  productsAsync?: Promise<(ReducedProductData | undefined)[]>
  products?: ReducedProductData[]
}

export const QuadProductImagesPreview: React.FC<QuadProductImagesPreviewProps> = ({
  productSkus,
  productCount,
  productsAsync,
  products,
  className,
  children,
  ...restProps
}) => {
  const count = productSkus?.length ?? productCount ?? 0
  return (
    <div
      className={cn(
        "grid aspect-square h-fit items-center gap-1 rounded-sm duration-200 ease-in-out @container motion-safe:transition group-hover/entry:motion-safe:scale-105",
        className ?? "",
        { "grid-cols-2 grid-rows-2": count != 1, "grid-cols-1 grid-rows-1": count == 1 }
      )}
      {...restProps}
    >
      <Suspense
        fallback={Array(count)
          .fill("")
          .map((_, i) => (
            <div key={i} className={"aspect-square size-auto rounded-sm bg-gray-400 motion-safe:animate-pulse"}></div>
          ))}
      >
        {products && <PreviewImagesSync products={products} productCount={count} />}
        {productsAsync && <QuadProductImagesPreviewServerPart productsAsync={productsAsync} productCount={count} />}
      </Suspense>
    </div>
  )
}

function PreviewImagesSync(props: { products: ReducedProductData[]; productCount: number }) {
  const products = props.products
    .filter((product) => product?.image != undefined)
    .slice(0, props.productCount > 4 ? 3 : 4)
  return (
    <>
      {products.map((product, index) => (
        <Image
          key={product?.id + "_" + index}
          src={product?.image ?? ""}
          alt={product?.description ?? ""}
          height={product?.image_height}
          width={product?.image_width}
          className={"m-auto h-full w-auto object-contain"}
        />
      ))}
      {props.productCount > 4 && (
        <div className="flex aspect-square h-full w-auto justify-center rounded-sm bg-gray-200 text-center align-middle text-xs text-gray-800 @[4.5rem]:text-lg">
          <span className={"my-auto inline-block h-fit"}>{`+${props.productCount - 3}`}</span>
        </div>
      )}
    </>
  )
}
